import React from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";

// Otter Assist admin — the ONE reason-required confirmation used by every high-impact action.
//
// Reason, internal note and the deliberate silent option are three DIFFERENT things (OAD-118) and are
// never collapsed into one field. The dialog always names the action, the target, the effect and
// whether it can be undone.

export function OaReasonDialog({
  open, onOpenChange, title, description, effect, target, reversible = true,
  confirmLabel = "Confirm", destructive = false, allowInternalNote = true, allowSilent = false,
  silentHint = "Only for abuse, fraud or security investigations. This is recorded in the audit log.",
  testId, mutationFn, invalidate = [], onDone,
}) {
  const qc = useQueryClient();
  const [reason, setReason] = React.useState("");
  const [note, setNote] = React.useState("");
  const [silent, setSilent] = React.useState(false);

  React.useEffect(() => {
    if (open) { setReason(""); setNote(""); setSilent(false); }
  }, [open]);

  const run = useMutation({
    mutationFn: () => mutationFn({ reason: reason.trim(), internal_note: note.trim() || null,
                                   silent }),
    onSuccess: (data) => {
      toast.success("Done — recorded in the audit log.");
      invalidate.forEach((key) => qc.invalidateQueries({ queryKey: key }));
      onOpenChange(false);
      if (onDone) onDone(data);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "That didn't work."),
  });

  const tooShort = reason.trim().length < 3;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid={`oa-reason-dialog-${testId}`}>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        <dl className="space-y-1.5 rounded-md border border-border bg-secondary/40 p-3 text-[12px]">
          {target && (
            <div className="flex gap-2"><dt className="w-20 shrink-0 text-muted-foreground">Target</dt>
              <dd className="font-mono text-[11px] break-all" data-testid={`oa-reason-target-${testId}`}>{target}</dd></div>
          )}
          <div className="flex gap-2"><dt className="w-20 shrink-0 text-muted-foreground">Effect</dt>
            <dd data-testid={`oa-reason-effect-${testId}`}>{effect}</dd></div>
          <div className="flex gap-2"><dt className="w-20 shrink-0 text-muted-foreground">Undo</dt>
            <dd className={reversible ? "" : "font-[600] text-destructive"}
              data-testid={`oa-reason-reversible-${testId}`}>
              {reversible ? "Reversible" : "This cannot be undone"}
            </dd></div>
        </dl>

        <div className="space-y-1.5">
          <label className="text-[11px] font-[600] uppercase tracking-wide text-muted-foreground">
            Reason (required)
          </label>
          <Textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)}
            placeholder="Why is this action being taken?" className="text-[13px]"
            data-testid={`oa-reason-input-${testId}`} />
        </div>

        {allowInternalNote && (
          <div className="space-y-1.5">
            <label className="text-[11px] font-[600] uppercase tracking-wide text-muted-foreground">
              Internal note (optional, admin-only)
            </label>
            <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)}
              placeholder="Context for other admins. The member never sees this."
              className="text-[13px]" data-testid={`oa-note-input-${testId}`} />
          </div>
        )}

        {allowSilent && (
          <label className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-2.5 text-[12px] text-amber-900">
            <Checkbox checked={silent} onCheckedChange={(v) => setSilent(!!v)}
              data-testid={`oa-silent-${testId}`} />
            <span><span className="font-[600]">Do not notify the member.</span> {silentHint}</span>
          </label>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}
            data-testid={`oa-reason-cancel-${testId}`}>Cancel</Button>
          <Button variant={destructive ? "destructive" : "default"}
            disabled={tooShort || run.isPending} onClick={() => run.mutate()}
            data-testid={`oa-reason-confirm-${testId}`}>
            {run.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            {confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default OaReasonDialog;
